/**
 * Preview detection — the small part of preview that every page must be able to
 * ask about.
 *
 * Split out of optimizely-preview.js so the hot paths (use-optimizely-page on
 * every page, the PDP review card) can answer "is preview on?" without pulling
 * the draft-merge machinery into the main bundle. The merge itself, the widget
 * and the link backfill stay behind dynamic imports and only load once an author
 * actually turns preview on.
 */

export const PREVIEW_SESSION_KEY = 'optimizely_cache_bypass'
export const PROJECT_ID_KEY = 'optimizely_preview_project_id'

export const getPreviewParamFromUrl = () => {
    if (typeof window === 'undefined') return null
    try {
        return new URL(window.location.href).searchParams.get('preview')
    } catch {
        return null
    }
}

// Active preview value from the cookie (the password in prod), or null if off.
export const getPreviewSession = () => {
    if (typeof window === 'undefined') return null
    try {
        const entry = document.cookie
            .split(';')
            .map((c) => c.trim())
            .find((c) => c.startsWith(`${PREVIEW_SESSION_KEY}=`))
        if (!entry) return null
        return decodeURIComponent(entry.slice(PREVIEW_SESSION_KEY.length + 1)) || null
    } catch {
        return null
    }
}

// Persist (truthy value) or clear (falsy / 'false') the preview session cookie.
export const setPreviewSession = (value) => {
    if (typeof window === 'undefined') return
    try {
        if (value && value !== 'false') {
            const encoded = encodeURIComponent(value)
            document.cookie = `${PREVIEW_SESSION_KEY}=${encoded}; path=/; SameSite=Lax`
        } else {
            document.cookie = `${PREVIEW_SESSION_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`
        }
    } catch {
        // document.cookie unavailable — silent no-op
    }
}

/**
 * Reconcile the ?preview URL param into the cookie session, then return the
 * active preview value (or null). ?preview=<value> enters, ?preview=false exits,
 * and a bare ?preview (empty value) enters with a 'true' sentinel.
 */
export const syncPreviewSession = () => {
    const param = getPreviewParamFromUrl()
    if (param === null) return getPreviewSession() // no param — keep current session
    if (param === 'false') setPreviewSession(null) // explicit exit
    else setPreviewSession(param || 'true') // enter; bare ?preview -> 'true'
    return getPreviewSession()
}

/**
 * Get preview project ID from session storage
 * @returns {string|null} Project ID or null
 */
export const getPreviewProjectId = () => {
    if (typeof window === 'undefined') return null
    try {
        return sessionStorage.getItem(PROJECT_ID_KEY)
    } catch {
        return null
    }
}

/** True when this request should load the preview machinery at all. */
export const isPreviewSessionActive = () => {
    if (typeof window === 'undefined') return false
    return Boolean(syncPreviewSession()) || Boolean(getPreviewProjectId())
}

/**
 * Get language code (for compatibility - simplified implementation)
 * @param {string} locale - Locale string
 * @returns {string} Language code
 */
export const getLanguageCode = (locale) => {
    return locale?.split('-')[0] || 'en'
}
